/**
 * Utility functions for handling localStorage
 */

/**
 * Estimates the size of a string in bytes
 * @param {string} str - The string to measure
 * @returns {number} - Size in bytes
 */
export const getStringSize = (str) => {
    // In JavaScript, strings are UTF-16 encoded (2 bytes per character)
    return str.length * 2;
};

/**
 * Estimates the size of any JavaScript object in bytes
 * @param {any} obj - The object to measure
 * @returns {number} - Estimated size in bytes
 */
export const getObjectSize = (obj) => {
    try {
        const str = JSON.stringify(obj);
        return getStringSize(str);
    } catch (error) {
        console.warn('Error calculating object size:', error);
        return 0;
    }
};

/**
 * Enhanced memory monitoring with crash prevention
 * @returns {Object} Detailed memory status
 */
export const getDetailedMemoryStatus = () => {
    const status = {
        jsHeap: null,
        localStorage: null,
        warning: false,
        critical: false,
        recommendations: []
    };

    // JavaScript heap memory (Chrome only)
    if (typeof window !== 'undefined' && window.performance && window.performance.memory) {
        const memory = window.performance.memory;
        const pressure = memory.usedJSHeapSize / memory.jsHeapSizeLimit;

        status.jsHeap = {
            used: memory.usedJSHeapSize,
            total: memory.totalJSHeapSize,
            limit: memory.jsHeapSizeLimit,
            pressure: pressure,
            usedMB: (memory.usedJSHeapSize / (1024 * 1024)).toFixed(2),
            limitMB: (memory.jsHeapSizeLimit / (1024 * 1024)).toFixed(2),
            available: memory.jsHeapSizeLimit - memory.usedJSHeapSize,
            availableMB: ((memory.jsHeapSizeLimit - memory.usedJSHeapSize) / (1024 * 1024)).toFixed(2)
        };

        if (pressure > 0.8) {
            status.critical = true;
            status.recommendations.push('Critical memory pressure detected - immediate cleanup needed');
        } else if (pressure > 0.6) {
            status.warning = true;
            status.recommendations.push('High memory usage detected');
        }
    }

    // localStorage usage
    try {
        status.localStorage = getLocalStorageInfo();

        const lsPressure = status.localStorage.used / status.localStorage.total;
        if (lsPressure > 0.8) {
            status.critical = true;
            status.recommendations.push('localStorage nearly full');
        } else if (lsPressure > 0.6) {
            status.warning = true;
            status.recommendations.push('localStorage usage high');
        }
    } catch (error) {
        status.localStorage = { error: error.message };
    }

    return status;
};

/**
 * Emergency memory protection - prevents browser crashes
 * @returns {Promise<boolean>} - True if safe to continue, false if should abort
 */
export const emergencyMemoryCheck = async () => {
    try {
        const status = getDetailedMemoryStatus();

        if (status.critical) {
            console.warn('🚨 CRITICAL MEMORY WARNING - Performing emergency cleanup');

            // Aggressive cleanup
            await performMemoryCleanup();

            // Force multiple garbage collections if available
            if (typeof window !== 'undefined' && window.gc) {
                for (let i = 0; i < 3; i++) {
                    window.gc();
                    await new Promise(resolve => setTimeout(resolve, 50));
                }
            }

            // Clear any large caches
            if (typeof caches !== 'undefined') {
                try {
                    const cacheNames = await caches.keys();
                    for (const cacheName of cacheNames) {
                        if (cacheName.includes('temp') || cacheName.includes('image')) {
                            await caches.delete(cacheName);
                        }
                    }
                } catch (cacheError) {
                    console.warn('Cache cleanup failed:', cacheError);
                }
            }

            // Wait for cleanup to take effect
            await new Promise(resolve => setTimeout(resolve, 200));

            // Check again
            const statusAfter = getDetailedMemoryStatus();
            if (statusAfter.critical) {
                console.error('🚨 Memory still critical after cleanup - operation should abort');
                return false;
            }
        }

        return true;
    } catch (error) {
        console.error('Emergency memory check failed:', error);
        return false;
    }
};

/**
 * Gets the approximate remaining localStorage space in bytes
 * @returns {object} - Object containing used, total and available space in bytes
 */
export const getLocalStorageInfo = () => {
    let totalUsed = 0;
    const items = { ...localStorage };

    // Calculate current usage
    for (const key in items) {
        if (items.hasOwnProperty(key)) {
            totalUsed += getStringSize(key) + getStringSize(items[key]);
        }
    }

    // SIMPLIFIED: Use a conservative estimate instead of testing to capacity
    // This prevents creating massive test files that consume storage
    const estimatedTotalSpace = 5 * 1024 * 1024; // Conservative 5MB estimate

    return {
        used: totalUsed,
        total: estimatedTotalSpace,
        available: estimatedTotalSpace - totalUsed,
        usedMB: (totalUsed / (1024 * 1024)).toFixed(2),
        totalMB: (estimatedTotalSpace / (1024 * 1024)).toFixed(2),
        availableMB: ((estimatedTotalSpace - totalUsed) / (1024 * 1024)).toFixed(2)
    };
};

/**
 * Checks if there's enough space to store an item in localStorage
 * @param {string} key - The key to store
 * @param {any} value - The value to store
 * @returns {boolean} - True if there's enough space, false otherwise
 */
export const hasEnoughSpace = (key, value) => {
    const valueSize = typeof value === 'string'
        ? getStringSize(value)
        : getObjectSize(value);

    const keySize = getStringSize(key);
    const requiredSpace = keySize + valueSize;

    const { available } = getLocalStorageInfo();

    return available > requiredSpace;
};

/**
 * Safely sets an item in localStorage, checking for available space first
 * @param {string} key - The key to store
 * @param {any} value - The value to store (will be JSON stringified if not a string)
 * @returns {boolean} - True if successful, false if not enough space
 */
export const safeSetItem = (key, value) => {
    try {
        const valueToStore = typeof value === 'string' ? value : JSON.stringify(value);

        // Check if we have enough space
        if (!hasEnoughSpace(key, valueToStore)) {
            console.warn(`localStorage quota would be exceeded: Trying to store ${key} (${(getStringSize(key) + getStringSize(valueToStore)) / 1024} KB)`);
            return false;
        }

        localStorage.setItem(key, valueToStore);
        return true;
    } catch (error) {
        console.error('Error storing data in localStorage:', error);
        return false;
    }
};

/**
 * Fast version of localStorage.setItem that doesn't perform space checks
 * Use this for operations that reduce storage size (like removing items)
 * @param {string} key - The key to store
 * @param {any} value - The value to store (will be JSON stringified if not a string)
 * @returns {boolean} - True if successful
 */
export const fastSetItem = (key, value) => {
    try {
        const valueToStore = typeof value === 'string' ? value : JSON.stringify(value);
        localStorage.setItem(key, valueToStore);
        return true;
    } catch (error) {
        console.error('Error storing data in localStorage:', error);
        return false;
    }
};

/**
 * Compresses a data URL image by reducing quality
 * @param {string} dataUrl - The original data URL
 * @param {number} quality - Quality factor (0.1 to 1.0)
 * @returns {Promise<string>} - Compressed data URL
 */
export const compressImageDataUrl = (dataUrl, quality = 0.7) => {
    return new Promise((resolve) => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        const img = new Image();

        img.onload = () => {
            // Set canvas size to be smaller for additional compression
            const maxSize = 400; // Maximum dimension
            let { width, height } = img;

            if (width > height && width > maxSize) {
                height = (height * maxSize) / width;
                width = maxSize;
            } else if (height > maxSize) {
                width = (width * maxSize) / height;
                height = maxSize;
            }

            canvas.width = width;
            canvas.height = height;

            // Draw and compress
            ctx.drawImage(img, 0, 0, width, height);
            const compressedDataUrl = canvas.toDataURL('image/jpeg', quality);
            resolve(compressedDataUrl);
        };

        img.onerror = () => {
            // If compression fails, return original
            resolve(dataUrl);
        };

        img.src = dataUrl;
    });
};

/**
 * Optimizes cart items to reduce storage usage
 * @param {Array} cartItems - Array of cart items
 * @returns {Array} - Optimized cart items
 */
export const optimizeCartItems = (cartItems) => {
    if (!Array.isArray(cartItems)) return cartItems;

    return cartItems.map(item => {
        // Create a copy of the item to avoid modifying the original
        const optimizedItem = { ...item };

        // Check if the item has an imageUrl that's a data URL
        if (optimizedItem.imageUrl && optimizedItem.imageUrl.startsWith('data:image')) {
            // Calculate the size of the data URL in KB
            const imageSize = (optimizedItem.imageUrl.length * 2) / 1024;

            // Be much more conservative - only optimize if the image is extremely large (over 2MB)
            if (imageSize > 2048) {
                console.log(`Optimizing extremely large data URL (${imageSize.toFixed(2)}KB) in cart item: ${optimizedItem.id}`);
                optimizedItem.imageUrl = '/images/products/default-map.jpg';
                optimizedItem._imageOptimized = true;
            } else {
                console.log(`Keeping data URL (${imageSize.toFixed(2)}KB) for cart item: ${optimizedItem.id} - within acceptable size limit`);
            }
        }

        return optimizedItem;
    });
};

/**
 * Safely adds a simple item to the cart (for gift cards and other non-image items)
 * @param {Object} newItem - The item to add to the cart
 * @returns {boolean} - True if successful, false if failed
 */
export const safeAddToCartSync = (newItem) => {
    try {
        // Get current cart
        let cart = JSON.parse(localStorage.getItem('cart') || '[]');

        // Add the new item
        cart.push(newItem);

        // Try to store the cart
        if (safeSetItem('cart', cart)) {
            return true;
        }

        // If storage failed for a simple item, something is seriously wrong
        console.error('Failed to add simple item to cart - localStorage may be full');
        return false;
    } catch (error) {
        console.error('Error in safeAddToCartSync:', error);
        return false;
    }
};

/**
 * Safely adds an item to the cart, optimizing if necessary (for complex items with images)
 * @param {Object} newItem - The item to add to the cart
 * @returns {Promise<boolean>} - True if successful, false if failed
 */
export const safeAddToCart = async (newItem) => {
    try {
        // Emergency memory check before starting
        const memoryOk = await emergencyMemoryCheck();
        if (!memoryOk) {
            console.error('🚨 Cannot proceed - memory too low');
            throw new Error('Insufficient memory to add item to cart');
        }

        // Simple memory check - just log, don't prevent operation
        if (typeof window !== 'undefined' && window.performance && window.performance.memory) {
            const memInfo = window.performance.memory;
            const memoryPressure = memInfo.usedJSHeapSize / memInfo.jsHeapSizeLimit;
            if (memoryPressure > 0.85) {
                console.warn('High memory pressure detected:', memoryPressure);
            }
        }

        // Get current cart with error handling
        let cart;
        try {
            cart = JSON.parse(localStorage.getItem('cart') || '[]');
            if (!Array.isArray(cart)) {
                console.warn('Cart data corrupted, resetting to empty array');
                cart = [];
            }
        } catch (parseError) {
            console.error('Error parsing cart data, resetting:', parseError);
            cart = [];
        }

        // Add the new item
        cart.push(newItem);

        // Try to store the cart directly first
        try {
            if (safeSetItem('cart', cart)) {
                return true;
            }
        } catch (storageError) {
            console.warn('Initial storage failed, trying optimization:', storageError.message);
        }

        // If direct storage failed, try with image compression
        if (newItem.imageUrl && newItem.imageUrl.startsWith('data:image')) {
            try {
                console.log('Compressing image for storage...');
                const compressedImageUrl = await compressImageDataUrl(newItem.imageUrl, 0.6);

                // Update the cart item with compressed image
                cart[cart.length - 1] = {
                    ...newItem,
                    imageUrl: compressedImageUrl,
                    _imageCompressed: true
                };

                if (safeSetItem('cart', cart)) {
                    console.log('Successfully stored with compressed image');
                    return true;
                }

                // If still failing, try with more aggressive compression
                const heavyCompressedImageUrl = await compressImageDataUrl(newItem.imageUrl, 0.3);
                cart[cart.length - 1] = {
                    ...newItem,
                    imageUrl: heavyCompressedImageUrl,
                    _imageCompressed: true
                };

                if (safeSetItem('cart', cart)) {
                    console.log('Successfully stored with heavily compressed image');
                    return true;
                }

            } catch (compressionError) {
                console.warn('Image compression failed:', compressionError);
            }
        }

        // As a final fallback, use default image
        console.log('Using default image as fallback');
        cart[cart.length - 1] = {
            ...newItem,
            imageUrl: '/images/products/default-map.jpg',
            _imageOptimized: true
        };

        return safeSetItem('cart', cart);

    } catch (error) {
        console.error('Error in safeAddToCart:', error);

        // Try one more time with minimal item as absolute last resort
        try {
            const minimalItem = {
                id: newItem.id,
                name: newItem.name,
                price: newItem.price,
                quantity: newItem.quantity,
                imageUrl: '/images/products/default-map.jpg',
                date: newItem.date,
                location: newItem.location,
                caption: newItem.caption,
                _minimal: true
            };

            let cart = JSON.parse(localStorage.getItem('cart') || '[]');
            cart.push(minimalItem);
            return safeSetItem('cart', cart);
        } catch (minimalError) {
            console.error('Even minimal item failed:', minimalError);
            return false;
        }
    }
};

/**
 * Performs comprehensive localStorage cleanup
 * @returns {Promise<number>} - Amount of space freed in bytes
 */
export const performLocalStorageCleanup = async () => {
    let freedSpace = 0;

    try {
        console.log('🧹 Starting localStorage cleanup...');

        const keysToRemove = [];

        // Find problematic items
        for (const key in localStorage) {
            if (localStorage.hasOwnProperty(key)) {
                const value = localStorage[key];
                const size = (key.length + value.length) * 2;

                // Remove test data, temp data, or items over 500KB
                if (key.includes('storage_test') ||
                    key.includes('___storage_test___') ||
                    key.includes('test_') ||
                    key.includes('temp_') ||
                    key.includes('debug_') ||
                    size > 512 * 1024) { // Over 500KB

                    keysToRemove.push({ key, size });
                    console.log(`🗑️ Marking for removal: ${key} (${(size / (1024 * 1024)).toFixed(2)}MB)`);
                }
            }
        }

        // Remove the problematic items
        keysToRemove.forEach(({ key, size }) => {
            try {
                localStorage.removeItem(key);
                freedSpace += size;
                console.log(`✅ Removed: ${key}`);
            } catch (error) {
                console.warn(`Failed to remove ${key}:`, error);
            }
        });

        console.log(`🧹 localStorage cleanup complete. Freed ${(freedSpace / (1024 * 1024)).toFixed(2)}MB`);
        return freedSpace;

    } catch (error) {
        console.error('Error during localStorage cleanup:', error);
        return 0;
    }
};

/**
 * Performs memory cleanup to prevent crashes
 * @returns {Promise<void>}
 */
export const performMemoryCleanup = async () => {
    try {
        console.log('🧹 Starting comprehensive memory cleanup...');

        // Force garbage collection if available (Chrome with --js-flags=--expose-gc)
        if (typeof window !== 'undefined' && window.gc) {
            console.log('Performing garbage collection...');
            window.gc();
            await new Promise(resolve => setTimeout(resolve, 50));
        }

        // Clear any large temporary variables that might be hanging around
        if (typeof window !== 'undefined') {
            // Clear any cached images or large objects
            if (window.cachedImages) {
                window.cachedImages = {};
            }

            // Clear any temporary canvas elements
            const tempCanvases = document.querySelectorAll('canvas[data-temp="true"]');
            tempCanvases.forEach(canvas => {
                const ctx = canvas.getContext('2d');
                if (ctx) {
                    ctx.clearRect(0, 0, canvas.width, canvas.height);
                }
                canvas.remove();
            });

            // Clear any SVG elements that might be consuming memory
            const largeSVGs = document.querySelectorAll('svg[data-large="true"]');
            largeSVGs.forEach(svg => svg.remove());

            // Clear any temporary blob URLs
            if (window.tempBlobUrls) {
                window.tempBlobUrls.forEach(url => URL.revokeObjectURL(url));
                window.tempBlobUrls = [];
            }
        }

        console.log('✅ Memory cleanup completed');
    } catch (error) {
        console.warn('Error during memory cleanup:', error);
    }
};

/**
 * Monitors memory usage and performs cleanup if needed
 * @returns {Object} Memory status information
 */
export const monitorMemoryUsage = () => {
    if (typeof window === 'undefined' || !window.performance || !window.performance.memory) {
        return { available: true, pressure: 0 };
    }

    const memInfo = window.performance.memory;
    const memoryPressure = memInfo.usedJSHeapSize / memInfo.jsHeapSizeLimit;

    const status = {
        used: memInfo.usedJSHeapSize,
        total: memInfo.totalJSHeapSize,
        limit: memInfo.jsHeapSizeLimit,
        pressure: memoryPressure,
        available: memoryPressure < 0.8,
        usedMB: (memInfo.usedJSHeapSize / (1024 * 1024)).toFixed(2),
        limitMB: (memInfo.jsHeapSizeLimit / (1024 * 1024)).toFixed(2)
    };

    // If memory pressure is high, perform cleanup
    if (memoryPressure > 0.8) {
        console.warn('🚨 High memory pressure detected:', memoryPressure);
        performMemoryCleanup();
    }

    return status;
};

/**
 * Creates a memory monitoring widget for debugging
 * @returns {HTMLElement} Memory monitor element
 */
export const createMemoryMonitor = () => {
    const monitor = document.createElement('div');
    monitor.id = 'memory-monitor';
    monitor.style.cssText = `
        position: fixed;
        top: 10px;
        right: 10px;
        background: rgba(0,0,0,0.8);
        color: white;
        padding: 10px;
        font-family: monospace;
        font-size: 12px;
        border-radius: 5px;
        z-index: 9999;
        min-width: 200px;
    `;

    const updateMonitor = () => {
        const status = getDetailedMemoryStatus();
        monitor.innerHTML = `
            <div><strong>Memory Monitor</strong></div>
            ${status.jsHeap ? `
                <div>JS Heap: ${status.jsHeap.usedMB}MB / ${status.jsHeap.limitMB}MB</div>
                <div>Pressure: ${(status.jsHeap.pressure * 100).toFixed(1)}%</div>
                <div style="background: ${status.jsHeap.pressure > 0.8 ? 'red' : status.jsHeap.pressure > 0.6 ? 'orange' : 'green'}; height: 5px; margin: 2px 0;"></div>
            ` : '<div>JS Heap: Not available</div>'}
            ${status.localStorage ? `
                <div>LocalStorage: ${status.localStorage.usedMB}MB / ${status.localStorage.totalMB}MB</div>
            ` : '<div>LocalStorage: Error</div>'}
            ${status.recommendations.length > 0 ? `
                <div style="color: orange; margin-top: 5px;">
                    ${status.recommendations.join('<br>')}
                </div>
            ` : ''}
        `;
    };

    updateMonitor();
    setInterval(updateMonitor, 2000); // Update every 2 seconds

    return monitor;
}; 